import { useCallback, useEffect, useMemo, useState } from 'react';
import { usePatientVirtualAppointments } from '../../hooks/useVirtualAppointments';
import type { VirtualAppointmentDto } from '../../services/virtualAppointments';
import { resolveDocImagesInBackground } from '../../utils/docImageResolution';
import { applyImageUrlsToDtos } from './virtualAppointmentImages';
import {
    partitionOpenVirtualAppointments,
    type OpenVirtualAppointments
} from './openVirtualAppointments';
import type { VirtualAppointmentLoadStatus } from '@odo/shared-ui/clinical/virtualAppointments/types';

const IMAGE_SOURCE = 'VirtualAppointments.resolveOpenDescriptionImages';
const NO_VAS: VirtualAppointmentDto[] = [];
const NO_FILTER = {};

interface PatientOpenVirtualAppointmentsOptions {
    /** Reports 'loading' until a read made after mount lands, so cached VAs never decide. */
    fresh?: boolean;
    /** Orders planned week VAs by due day; without it only anchored ones have one. */
    treatmentStartDate?: string | null;
}

export interface PatientOpenVirtualAppointments extends OpenVirtualAppointments {
    status: VirtualAppointmentLoadStatus;
    retry: () => void;
}

// A patient's open VAs across every document, plus the round's cards (open or done since
// `roundStartedAt`), the planned and the past ones, with their description images resolved.
export function usePatientOpenVirtualAppointments(
    patientId: string,
    roundStartedAt: string | null = null,
    { fresh = false, treatmentStartDate = null }: PatientOpenVirtualAppointmentsOptions = {}
): PatientOpenVirtualAppointments {
    const { data, isPending, isError, isFetching, isFetchedAfterMount, refetch } =
        usePatientVirtualAppointments(patientId, NO_FILTER, { fresh });
    const partition = useMemo(
        () => partitionOpenVirtualAppointments(data ?? NO_VAS, roundStartedAt, treatmentStartDate),
        [data, roundStartedAt, treatmentStartDate]
    );
    const [imageUrls, setImageUrls] = useState<Record<string, string>>({});

    useEffect(() => {
        const docs = [...partition.round, ...partition.planned, ...partition.history].flatMap(
            (va) => (va.descriptionDoc ? [va.descriptionDoc] : [])
        );
        return resolveDocImagesInBackground(patientId, docs, IMAGE_SOURCE, (urls) =>
            setImageUrls((prev) => ({ ...prev, ...urls }))
        );
    }, [patientId, partition.round, partition.planned, partition.history]);

    const round = useMemo(
        () => applyImageUrlsToDtos(partition.round, imageUrls),
        [partition.round, imageUrls]
    );
    const retry = useCallback(() => void refetch(), [refetch]);
    const failed = isError && !isFetching;
    const settling = fresh && !isFetchedAfterMount;
    const status: VirtualAppointmentLoadStatus = failed
        ? 'error'
        : isPending || isError || settling
          ? 'loading'
          : 'ready';
    const planned = useMemo(
        () => applyImageUrlsToDtos(partition.planned, imageUrls),
        [partition.planned, imageUrls]
    );
    const history = useMemo(
        () => applyImageUrlsToDtos(partition.history, imageUrls),
        [partition.history, imageUrls]
    );
    const open = useMemo(
        () => applyImageUrlsToDtos(partition.open, imageUrls),
        [partition.open, imageUrls]
    );
    return useMemo(
        () => ({ open, round, planned, history, status, retry }),
        [open, round, planned, history, status, retry]
    );
}
